import { HelpCircle } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { StepHeader } from '~/components/forms/StepHeader';

/**
 * IntentStep - First step asking if family plans to re-enroll
 */
export function IntentStep({ formData, schoolYearName, updateFormData }: any) {
  const intent = formData?.intent || {};
  const isReenrolling = intent.isReenrolling;

  const handleIntentChange = (value: string) => {
    updateFormData('intent', {
      ...intent,
      isReenrolling: value === 'yes',
      declineReason: value === 'yes' ? null : intent.declineReason
    });
  };

  const handleDeclineReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData('intent', {
      ...intent,
      declineReason: e.target.value
    });
  };

  return (
    <div className="space-y-6">
      <StepHeader
        icon={HelpCircle}
        title={`Re-enrollment for ${schoolYearName}`}
        description="We're excited to begin planning for the upcoming school year. Please let us know your family's intentions."
      />

      <Card className="max-w-xl mx-auto">
        <CardHeader>
          <CardTitle>Is your family planning to re-enroll?</CardTitle>
          <CardDescription>
            This helps us plan class sizes and staffing for the upcoming year.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup
            value={isReenrolling === true ? 'yes' : isReenrolling === false ? 'no' : undefined}
            onValueChange={handleIntentChange}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
              <RadioGroupItem value="yes" id="intent-yes" />
              <Label htmlFor="intent-yes" className="flex-1 cursor-pointer">
                <span className="font-medium">Yes, we plan to re-enroll</span>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Continue to confirm your family's information and students
                </p>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
              <RadioGroupItem value="no" id="intent-no" />
              <Label htmlFor="intent-no" className="flex-1 cursor-pointer">
                <span className="font-medium">No, we will not be returning</span>
                <p className="text-sm text-muted-foreground mt-0.5">
                  We understand and wish your family all the best
                </p>
              </Label>
            </div>
          </RadioGroup>

          {isReenrolling === false && (
            <div className="pt-4 border-t space-y-3">
              <Label htmlFor="declineReason">
                Would you like to share why you're leaving? (optional)
              </Label>
              <Textarea
                id="declineReason"
                value={intent.declineReason || ''}
                onChange={handleDeclineReasonChange}
                placeholder="Your feedback helps us improve..."
                rows={3}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {isReenrolling === false && (
        <p className="text-center text-sm text-muted-foreground">
          Click "Submit" to complete this form. Thank you for your time with us.
        </p>
      )}

      {isReenrolling === true && (
        <p className="text-center text-sm text-muted-foreground">
          Click "Next" to continue with the re-enrollment process.
        </p>
      )}
    </div>
  );
}

export default IntentStep;
